import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IReduxState } from 'src/store/reducers';
import {
  Card,
  Typography,
  Button,
  makeStyles,
  useMediaQuery,
  AccordionDetails,
  Accordion,
  AccordionSummary,
  Box,
} from '@material-ui/core';

import 'react-calendar/dist/Calendar.css';
import { ICoupon } from 'src/types';

import giftbox from 'src/assets/giftbox.png';
import installment from 'src/assets/installment.png';
import shield from 'src/assets/Shield.png';

import mixPanel from 'src/utils/mixpanel';
import { MIXPANEL_TRACK_FUNNEL_V2 } from 'src/utils/consts';
import { updateEstimateServiceStatusV2 } from 'src/api/quote';

import { setAppointment } from 'src/store/actions';

import {
  ArrowBack,
  ArrowForward,
  CheckCircle,
  ExpandMore,
  ToggleOff,
  ToggleOn,
} from '@material-ui/icons';
import InvoiceMobile from '../atoms/InvoiceMobile';
import ReferralBox from '../atoms/ReferralCode';
import GetBackStep from './GetBackStep';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    background: '#fff',
    '& .MuiAccordion-root:before': {
      height: 0,
    },
  },
  backButton: {
    marginRight: theme.spacing(1),
  },
  menuPaper: {
    maxHeight: 300,
    marginTop: '50px',
    fontFamily: 'inter',
    position: 'fixed',
  },
  continueButton: {
    marginLeft: theme.spacing(1),
  },
  timePickerContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    width: '100%',
    maxHeight: 320,
    margin: 'auto',
    overflowY: 'scroll',
    [theme.breakpoints.up('md')]: {
      width: '300px',
    },
  },
  verticalInputs: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      '& > *': {
        marginBottom: 10,
      },
    },
  },
  itemTimeSlot: {
    cursor: 'pointer',
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    padding: theme.spacing(1.5),
    display: 'flex',
    justifyContent: 'center',
    fontSize: 16,
    fontWeight: 500,
    color: '#7E7A92',

    background: theme.palette.common.white,

    '&.selected': {
      background: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
  },
  buttonForward: {
    color: '#fff',
    margin: '0px 20px 20px 20px',
    padding: '6px 24px',
    fontFamily: 'inter',
    borderRadius: '32px',
    textTransform: 'none',
  },
  greenCard: {
    backgroundColor: '#005959',
    padding: '24px 16px',
    flex: 1,
    color: '#fff',
  },
  whiteCard: {
    backgroundColor: theme.palette.common.white,
  },
  mobileRoot: {
    width: '100%',
  },
  TimeandLocation: {
    marginTop: 20,

    paddingBottom: 20,
    background: '#fff',
    marginBottom: 0,
    maxWidth: 350,
    width: '100%',
  },
  accordion: {
    '& .MuiAccordion-root:before': {
      height: 0,
    },
  },

  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: '100%',
  },
  Hdiv: {
    display: 'flex',
    flexDirection: 'row',
  },
  Radio: {
    border: '2px solid var(--blue-50, #227596)',
    borderRadius: 8,
    padding: '7px 7px',
    margin: 'auto',
    marginTop: 10,
    color: '#003E3E',
    width: '100%',
    background: '#E6EEEE',
    fontSize: 17,
    [theme.breakpoints.down('sm')]: {
      fontSize: 15,
    },
  },
  RadioUnSelected: {
    border: '1px solid #A5A7A7',
    borderRadius: 8,
    padding: '7px 7px',
    margin: 'auto',
    marginTop: 10,
    color: '#003E3E',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      fontSize: 15,
    },
  },
  RadioButton: {
    color: '#003E3E',
    '& .checked': {
      color: '#003E3E',
    },
  },
  formControl: {
    '& .span.MuiTypography-root.MuiFormControlLabel-label.MuiTypography-body1':
      {
        width: '-webkit-fill-available',
      },
  },
  hrow: {
    width: '100%',
    margin: 'auto',
  },
}));

interface EstimateStepProps {
  onNext: () => void;
  onBack: () => void;
  bookingLink: boolean;
}

type ExpandedState = {
  [key: string]: boolean;
};

const EstimateStep: React.FC<EstimateStepProps> = ({
  onNext,
  onBack,
  bookingLink,
}) => {
  const classes = useStyles();
  const isSm = useMediaQuery('(max-width: 1300px)');

  const appointment = useSelector(
    (state: IReduxState) => state.quote.appointment
  );
  const isFleetJob = !!appointment?.attributes?.fleet_job;

  const dispatch = useDispatch();

  const createInitialState = () => {
    const state: ExpandedState = {};
    if (appointment?.attributes.estimate) {
      return Object.keys(appointment?.attributes.estimate.services)
        .map((_, index) => ({ [`panel${index}`]: true }))
        .reduce((acc, curr) => ({ ...acc, ...curr }), {});
    }
    return state;
  };

  const [expanded, setExpanded] = useState<ExpandedState>(createInitialState());

  const handleChange = (panel: string) => {
    setExpanded((prevExpanded) => ({
      ...prevExpanded,
      [panel]: !prevExpanded[panel],
    }));
  };

  const handleDeleteRepairProblem = (id: number, statusUpdate: string) => {
    const data = {
      estimate_service: {
        status: statusUpdate,
      },
    };

    updateEstimateServiceStatusV2(id, data).then((res) => {
      dispatch(setAppointment(res.data));
    });
  };

  const handleContinue = () => {
    if (!appointment) {
      return;
    }

    if (appointment.attributes.appointment_type === 'repair') {
      mixPanel(MIXPANEL_TRACK_FUNNEL_V2.NF_ESTIMATE);
    } else {
      mixPanel(MIXPANEL_TRACK_FUNNEL_V2.NF_DIAGNOSIS);
    }

    onNext();
  };

  const getPrice = () => {
    let price = 0;

    const estimate = appointment?.attributes.estimate;

    if (estimate) {
      if (
        !appointment?.attributes.member_job ||
        appointment?.attributes.shop_network
      ) {
        price += estimate.total_price;
      } else {
        price += estimate.total_member_price;
      }
    }

    if (appointment?.attributes.appointment_type === 'diagnosis')
      price += appointment.attributes.diagnosis_price;

    price = price >= 0 ? price : 0;

    return price.toFixed(2);
  };

  const getCouponText = (coupon: ICoupon) => {
    if (coupon.code_type === 'agent') {
      let txt = 'Your service writer';

      if (coupon.display_name) {
        txt += `, ${coupon.display_name}, `;
      } else {
        txt += ' ';
      }

      txt += `applied a $${coupon.dollars_off} discount just for you.`;
      return txt;
    }

    return `Coupon code applied: ${coupon.code}`;
  };

  return (
    <div style={{ maxWidth: '600px' }}>
      {!bookingLink &&
        !(
          appointment?.attributes.appointment_type === 'repair' &&
          !appointment?.attributes.estimate
        ) && (
          <Button
            className={classes.buttonForward}
            style={{
              color: '#005959',
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
            }}
            onClick={() => {
              onBack();
            }}
          >
            <ArrowBack
              style={{
                width: 20,
                height: 20,
                marginTop: 'auto',
                marginBottom: 'auto',
              }}
            />{' '}
            <Typography
              style={{
                margin: 'auto 0 auto 10px',
                fontSize: 16,
                fontFamily: 'Inter',
              }}
            >
              Back
            </Typography>
          </Button>
        )}
      {appointment?.attributes.appointment_type === 'repair' &&
      !appointment?.attributes.estimate ? (
        <GetBackStep />
      ) : (
        <Card
          className={classes.card}
          style={{
            marginLeft: 'auto',
            marginRight: 'auto',

            width: 'fit-content',
            background: '#F3F3F3',
          }}
          elevation={0}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              marginRight: isSm ? 0 : 20,
            }}
          >
            {appointment?.attributes.appointment_type === 'diagnosis' && (
              <Accordion
                style={{
                  background: '#fff',
                  borderRadius: 8,
                  border: '1px solid #005959',
                  marginBottom: 20,
                  boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.06)',
                }}
                elevation={1}
                expanded
              >
                <AccordionDetails
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 10,
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography
                      style={{
                        margin: '10px',
                        fontFamily: 'Inter',
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      Diagnosis
                    </Typography>
                  </div>

                  <Box style={{ margin: '10px' }}>
                    {appointment.attributes.diagnosis_fee && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: 10,
                          alignItems: 'center',
                        }}
                      >
                        <CheckCircle
                          style={{ color: '#005959', height: 25, width: 25 }}
                        />
                        <Typography
                          style={{
                            margin: '10px',
                            fontFamily: 'Inter',
                            fontSize: 14,
                            fontWeight: 600,
                          }}
                        >
                          Comprehensive diagnosis of your vehicle
                        </Typography>
                      </div>
                    )}

                    {appointment.attributes.estimate && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: 10,
                          alignItems: 'center',
                        }}
                      >
                        <CheckCircle
                          style={{ color: '#005959', height: 25, width: 25 }}
                        />
                        <Typography
                          style={{
                            margin: '10px',
                            fontFamily: 'Inter',
                            fontSize: 14,
                            fontWeight: 600,
                          }}
                        >
                          Option to pay for repairs in installments later
                        </Typography>
                      </div>
                    )}

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 10,
                        alignItems: 'center',
                      }}
                    >
                      <CheckCircle
                        style={{ color: '#005959', height: 25, width: 25 }}
                      />
                      <Typography
                        style={{
                          margin: '10px',
                          fontFamily: 'Inter',
                          fontSize: 14,
                          fontWeight: 600,
                        }}
                      >
                        Our technician will come to you
                      </Typography>
                    </div>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            <div>
              {appointment?.attributes.estimate && (
                <>
                  <div
                    style={{
                      maxHeight: 400,
                      overflowY: 'scroll',
                      marginBottom: 20,
                    }}
                  >
                    {Object.keys(appointment.attributes.estimate.services).map(
                      (service, index) => (
                        <Accordion
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          style={{
                            background: '#fff',

                            marginRight: 10,
                            borderRadius: 8,
                            marginBottom: 20,
                            boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.06)',
                          }}
                          className={classes.accordion}
                          elevation={1}
                          expanded={expanded[`panel${index}`] || false}
                          onChange={() => handleChange(`panel${index}`)}
                        >
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 20,
                                width: '100%',
                              }}
                            >
                              <Typography
                                style={{
                                  fontFamily: 'Inter',
                                  textDecoration:
                                    !isFleetJob &&
                                    appointment.attributes.estimate?.services[
                                      service
                                    ].status === 'rejected'
                                      ? 'line-through'
                                      : 'none',
                                  fontSize: 16,
                                  fontWeight: 700,
                                }}
                              >
                                {service}
                              </Typography>
                              <Typography
                                style={{
                                  fontFamily: 'Inter',
                                  textDecoration:
                                    !isFleetJob &&
                                    appointment.attributes.estimate?.services[
                                      service
                                    ].status === 'rejected'
                                      ? 'line-through'
                                      : 'none',
                                  fontSize: 16,
                                  fontWeight: 700,
                                }}
                              >
                                $
                                {Number(
                                  appointment.attributes.estimate?.services[
                                    service
                                  ].total_price
                                ).toFixed(2)}
                              </Typography>
                            </div>
                          </AccordionSummary>
                          {appointment &&
                            appointment.attributes.estimate?.services[service]
                              .customer_to_supply_parts && (
                              <Typography
                                style={{
                                  fontSize: 12,
                                  color: 'grey',
                                  fontFamily: 'Inter',
                                  fontStyle: 'Italic',
                                  marginLeft: 10,
                                }}
                              >
                                * Customer supplies parts
                              </Typography>
                            )}
                          <div
                            style={{
                              marginLeft: 10,
                              marginRight: 10,
                              borderTop: '0.2px solid lightgrey',
                            }}
                          />
                          <AccordionDetails>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              {appointment.attributes.estimate?.services[
                                service
                              ].description && (
                                <Typography
                                  style={{
                                    color: '#646867',
                                    fontSize: 14,
                                    fontFamily: 'Inter',
                                    marginBottom: 12,
                                  }}
                                >
                                  {
                                    appointment.attributes.estimate?.services[
                                      service
                                    ].description
                                  }
                                </Typography>
                              )}
                              {appointment.attributes.estimate?.services[
                                service
                              ].parts.map((part) => {
                                return (
                                  <div>
                                    <Typography
                                      style={{
                                        fontSize: 16,
                                        display: 'flex',
                                        flexDirection: 'row',
                                        fontFamily: 'Inter',
                                      }}
                                    >
                                      {part.name} x({part.quantity}):&nbsp; $
                                      {part.unit_price}{' '}
                                    </Typography>
                                  </div>
                                );
                              })}

                              <Typography
                                style={{
                                  fontSize: 16,
                                  display: 'flex',
                                  flexDirection: 'row',
                                  fontFamily: 'Inter',
                                }}
                              >
                                Labor :&nbsp; $
                                {
                                  appointment.attributes.estimate?.services[
                                    service
                                  ].labor_price
                                }{' '}
                              </Typography>
                            </div>
                            {!isFleetJob &&
                              appointment.attributes.estimate &&
                              Object.keys(
                                appointment.attributes.estimate.services
                              ).length > 1 && (
                                <Button
                                  onClick={() => {
                                    if (appointment.attributes.estimate) {
                                      const serviceToUpdate =
                                        appointment.attributes.estimate
                                          ?.services[service];

                                      handleDeleteRepairProblem(
                                        serviceToUpdate.id,
                                        serviceToUpdate.status === 'rejected'
                                          ? 'approved'
                                          : 'rejected'
                                      );
                                    }
                                  }}
                                  style={{
                                    marginLeft: 'auto',
                                    color:
                                      appointment.attributes.estimate?.services[
                                        service
                                      ].status === 'rejected'
                                        ? 'red'
                                        : 'green',
                                    padding: 0,
                                  }}
                                >
                                  {appointment.attributes.estimate?.services[
                                    service
                                  ].status === 'rejected' ? (
                                    <ToggleOff />
                                  ) : (
                                    <ToggleOn />
                                  )}
                                </Button>
                              )}
                          </AccordionDetails>
                        </Accordion>
                      )
                    )}
                  </div>
                </>
              )}
            </div>

            {appointment?.attributes.coupon_used && (
              <div
                style={{
                  background: '#E3F2F9',
                  border: '1px solid #48ADD5',
                  display: 'flex',
                  maxWidth: 400,
                  gap: 20,
                  padding: '10px 20px',
                  borderRadius: 12,
                  marginBottom: 20,
                }}
              >
                <img
                  src={giftbox}
                  alt="Credit Card"
                  style={{ width: 20, height: 20, marginTop: 10 }}
                />

                <div style={{ display: 'flex' }}>
                  <Typography
                    style={{
                      fontSize: 16,
                      color: '#1A5B74',
                      fontFamily: 'Inter',
                    }}
                  >
                    {getCouponText(appointment.attributes.coupon_used)}
                  </Typography>
                </div>
              </div>
            )}

            {isSm && <ReferralBox />}
            {isSm && <InvoiceMobile onNext={handleContinue} />}

            <div
              style={{
                display: 'flex',
                maxWidth: 400,
                gap: 10,
                borderRadius: 12,
                alignItems: 'center',
                marginBottom: 20,
              }}
            >
              {' '}
              <img
                src={shield}
                alt="Credit Card"
                style={{ width: 20, height: 20 }}
              />{' '}
              <Typography
                style={{
                  fontSize: 16,
                  fontFamily: 'Inter',
                }}
              >
                2 year service warranty on all services
              </Typography>
            </div>

            {appointment?.attributes.estimate && (
              <div
                style={{
                  display: 'flex',
                  maxWidth: 400,
                  gap: 10,
                  borderRadius: 12,
                  marginBottom: 20,
                  alignItems: 'center',
                }}
              >
                {' '}
                <img
                  src={installment}
                  alt="Credit Card"
                  style={{ width: 20, height: 20 }}
                />{' '}
                <Typography
                  style={{
                    fontSize: 16,

                    fontFamily: 'Inter',
                  }}
                >
                  Option to pay for repairs in installments later
                </Typography>
              </div>
            )}
          </div>

          {!isSm && (
            <Button
              onClick={handleContinue}
              style={{
                background: '#005959',
                marginTop: '35px',
                marginLeft: '10px',
                color: '#fff',
                height: '56px',
                fontSize: '16px',
              }}
              className={classes.buttonForward}
              variant="contained"
              size="large"
            >
              Continue &nbsp;
              <ArrowForward />
            </Button>
          )}
        </Card>
      )}
    </div>
  );
};

export default EstimateStep;
